<template>
    <div class="pb-32 h-full bg-page">
        <div
            v-if="globalLoading"
            class="global-loading fixed z-50 left-0 top-0 w-full h-full min-h-screen"
        >
            <div
                class="relative flex items-center justify-center w-full h-full"
            >
                <div class="block"> </div>
                <div
                    class="spinner spinner-blue flex justify-center text-center"
                >
                    <span class="text-md block mb-36 text-white">
                        {{ $t('checkout.processingPayment') }}
                        <br />
                        {{ $t('checkout.dontCloseWindow') }}
                    </span>
                </div>
            </div>
        </div>
        <header
            class="fixed z-50 left-0 top-0 flex flex-row py-1 w-full bg-primary sm:py-0 shadow-sm"
        >
            <div class="z-50 pl-2 w-auto">
                <div
                    class="z-50 left-0 top-0 mr-4 mt-1 p-2.5 text-gray-700 bg-white rounded-full hover:shadow-lg shadow-md transition-shadow sm:hidden"
                    @click="$router.push({ name: 'menu' })"
                >
                    <chevron-left-icon class="w-5 h-5" />
                </div>
            </div>
            <div class="mx-auto px-14 w-full text-left sm:hidden">
                <h1
                    class="mb-3 ml-5 mt-3 text-heading font-heading text-xl font-black leading-tight sm:ml-0 sm:text-3xl"
                    >{{ $t('checkout.checkout') }}</h1
                >
            </div>
        </header>
        <main
            v-if="!globalLoading"
            class="checkout container-lg pt-3 max-w-6xl min-h-screen sm:pt-10 md:px-3 lg:px-6 xl:px-8"
        >
            <div class="checkout flex">
                <div class="checkout-details">
                    <div
                        :class="{
                            'mt-8': !isPickup,
                            'mt-0': isPickup
                        }"
                        class="checkout-details__info sm:mt-10 pb-3 px-2 md:px-3"
                    >
                        <h1
                            class="hidden mb-3 mt-2 text-heading font-heading text-4xl font-black leading-tight sm:block"
                            >{{ $t('checkout.checkout') }}</h1
                        >
                        <div
                            v-if="!isInStore"
                            class="hidden flex-row items-center bg-secondary relative p-3 border rounded shadow-xs"
                        >
                            <div class="flex flex-row mr-3 text-itembody">
                                <span class="">
                                    {{ fulfillmentMethodLabel }}
                                </span>
                            </div>
                            <div
                                class="flex flex-row items-center mr-4 text-itembody"
                            >
                                <clock-icon
                                    class="mr-1 w-4 h-4 text-itembody"
                                />
                                <span v-if="isPreOrder">
                                    {{ $t('checkout.preOrder') }}
                                </span>
                                <span v-else>
                                    {{ $t('checkout.asap') }}
                                    {{ prepTime }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div
                        :class="{
                            'pt-0 sm:pt-8': isPickup,
                            'pt-8': !isPickup
                        }"
                        class="checkout-details__payment p-2 bg-secondary rounded-md md:p-6 lg:p-8"
                    >
                        <div class="checkout__personal px-1 sm:pt-5 md:px-3">
                            <store-notice
                                :class="{
                                    'mt-8 sm:-mt-4': isPickup,
                                    '-mt-4': !isPickup
                                }"
                                class="mb-6 sm:mb-8"
                                location="basket"
                            />

                            <venue-pickup-location v-if="isPickup" />

                            <pre-order
                                v-if="isPreOrder"
                                id="pickup-date"
                                :availablePreorderDays="
                                    selectedMethodPreorderDays
                                "
                                :blockAutoDatePreFill="
                                    !!this.$route.query.id ||
                                    !!this.$route.query.redirectResult
                                "
                                :disableAll="disableAll"
                                :fulfillmentMethod="fulfillmentMethod"
                                :v="$v"
                                :venueId="venueId"
                                class="mb-4 md:mb-0"
                            />

                            <div
                                v-if="isPreOrder"
                                class="hidden my-8 sm:block sm:border-t sm:border-gray-200"
                            >
                            </div>

                            <customer-details
                                id="customer-details"
                                :disableAll="disableAll"
                                :is-loyalty-auth="customerAuth"
                                :loyalty-identifier-type="identifierType"
                                :v="$v.customer"
                                @phoneValidation="phoneValidation"
                            />

                            <delivery-address
                                v-if="isDelivery"
                                id="delivery-address"
                                :disableAll="disableAll"
                                :requiredFields="requiredFields"
                                :v="$v.deliveryAddress"
                            />

                            <policy-consents
                                v-if="
                                    !policyAgreedInLocalStorage && optInMessage
                                "
                                :accountId="accountId"
                                :disableAll="disableAll"
                                :optInMessage="optInMessage"
                                :venueId="venueId"
                            />

                            <piggy-checkbox
                                v-if="!billId"
                                :is-calculate="isCalculate"
                            />

                            <piggy-text
                                v-if="showEstimatedCredits && !billId"
                                :is-calculate="isCalculate"
                            />

                            <cutlery-checkbox v-if="askAboutCutlery" />

                            <guest-count-input v-if="hasGuestCountInput" />

                            <order-notes
                                v-if="!isMobile && oderNotesVisibility"
                                id="order-notes"
                                :v="$v.notes"
                            />

                            <span
                                v-if="
                                    this.comoEnabled &&
                                    this.comoPaymentWithPointsEnabled &&
                                    false
                                "
                                class="text-itembody text-sm font-black select-none cursor-pointer"
                                @click="$modal.show('pay-with-como-points')"
                                >{{ $t('checkout.payWithLoyaltyPoints') }}</span
                            >
                        </div>

                        <div
                            class="hidden mt-4 mx-1 border-t sm:block sm:border-gray-200 md:mx-3"
                        />

                        <tip-selection
                            v-if="showTipSelection"
                            :disableAll="disableAll"
                            class="pt-5 px-1 md:px-3"
                        />

                        <discount-code-box
                            :disableAll="disableAll"
                            :pickup-date="pickupDate"
                            :pickup-time-slot="pickupTimeSlot"
                        />

                        <gift-card-box
                            v-if="
                                piggySettings?.enabled &&
                                piggySettings?.giftCardsEnabled
                            "
                        />

                        <po-number v-if="isPoNumberEnabled" />

                        <div
                            v-if="
                                discountCodeDetails &&
                                discountCodeDetails.minimumAmount &&
                                discountCodeDetails.minimumAmount -
                                    cartSubtotal >
                                    0
                            "
                            class="mt-2 flex sm:hidden flex-auto px-1 flex-row justify-between text-subheading"
                        >
                            <span class="total">
                                {{ $t('checkout.discountCode') }}
                            </span>
                            <span
                                v-if="discountCodeDetails.minimumAmount"
                                class="price text-red-600"
                            >
                                {{ $t('checkout.spend') }}
                                {{
                                    ((discountCodeDetails.minimumAmount -
                                        cartSubtotal) /
                                        100)
                                        | currency
                                }}
                                {{ $t('checkout.more') }}
                            </span>
                        </div>

                        <div class="checkout__buy px-1 py-3 md:px-3 md:py-6">
                            <span
                                v-if="paymentError"
                                class="block py-3 text-red-500 text-sm"
                            >
                                {{ $t('checkout.errors.paymentError') }}
                            </span>
                            <div v-if="fulfillmentMethod" class="relative">
                                <countdown-button
                                    v-if="isCounting"
                                    @toggle-counting="toggleCounting"
                                    @complete-confirmation-step="
                                        validateAndCheckout(
                                            PaymentMethodsEnum.CASH
                                        )
                                    "
                                />
                                <button
                                    v-else-if="canAddToBill"
                                    :class="{ 'spinner white': isLoading }"
                                    :disabled="isLoading"
                                    class="border-button-primary py-3 w-full text-center text-button font-bold bg-button font-button clip-path-button rounded-button rounded-sm shadow-sm select-none"
                                    @click="
                                        hasAddToBillOrder
                                            ? toggleCounting()
                                            : validateAndCheckout(
                                                  PaymentMethodsEnum.CASH
                                              )
                                    "
                                >
                                    <span v-show="!isLoading">
                                        {{ $t('checkout.addToYourBill') }}
                                    </span>

                                    <span class="inline-block"></span>
                                </button>
                                <button
                                    v-else-if="totalWithDiscount === 0"
                                    :class="{ spinner: isLoading }"
                                    :disabled="isLoading"
                                    class="py-3 w-full text-button font-bold bg-button font-button clip-path-button rounded-button shadow-sm select-none"
                                    @click="
                                        validateAndCheckout(
                                            PaymentMethodsEnum.CASH
                                        )
                                    "
                                >
                                    <span v-show="!isLoading">
                                        Place
                                        {{
                                            isInStore
                                                ? ''
                                                : fulfillmentMethod.toLowerCase()
                                        }}
                                        order
                                    </span>
                                    <span class="inline-block"></span>
                                </button>
                                <adyen-checkout
                                    v-else-if="
                                        activePaymentSystem ===
                                        PaymentMethodsEnum.ADYEN
                                    "
                                    :checkoutObject="checkoutObject"
                                    :errors="errors || checkoutError"
                                    :is-loading="isLoading"
                                    :redirectResult="adyenRedirectResult"
                                    @cash-checkout="
                                        disableAllFields(true);
                                        validateAndCheckout(
                                            PaymentMethodsEnum.CASH
                                        );
                                    "
                                    @is-validated="isValidated"
                                    @disable-all-fields="disableAllFields"
                                />

                                <yoco-checkout
                                    v-else-if="
                                        activePaymentSystem ===
                                        PaymentMethodsEnum.YOCO
                                    "
                                    :checkoutObject="checkoutObject"
                                    :errors="errors || checkoutError"
                                    :is-loading="isLoading"
                                    :is-payment-result="!!$route.params.orderId"
                                    @is-validated="isValidated"
                                    @disable-all-fields="disableAllFields"
                                />

                                <button
                                    v-else-if="
                                        activePaymentSystem !==
                                            PaymentMethodsEnum.ADYEN &&
                                        activePaymentSystem !==
                                            PaymentMethodsEnum.YOCO
                                    "
                                    :class="{
                                        'spinner white':
                                            isLoading || zonalIsLoading
                                    }"
                                    :disabled="isLoading || zonalIsLoading"
                                    class="border-button-primary py-3 w-full text-center text-button font-bold bg-button font-button clip-path-button rounded-button rounded-sm shadow-sm select-none"
                                    @click="
                                        validateAndCheckout(
                                            PaymentMethodsEnum.CASH
                                        )
                                    "
                                >
                                    <span
                                        v-show="!isLoading && !zonalIsLoading"
                                    >
                                        Place
                                        {{
                                            isInStore
                                                ? ''
                                                : fulfillmentMethod.toLowerCase()
                                        }}

                                        {{ $t('checkout.order') }}
                                    </span>

                                    <span class="inline-block"></span>
                                </button>
                            </div>
                            <div v-else>
                                <button
                                    class="border-button-primary py-3 w-full text-center text-button font-bold bg-button font-button clip-path-button rounded-button rounded-sm shadow-sm select-none"
                                    @click="reloadPage()"
                                >
                                    <span>
                                        {{ $t('checkout.somethingWentWrong') }}
                                    </span>

                                    <span class="inline-block"></span>
                                </button>
                            </div>
                        </div>
                        <div v-if="!billId" class="px-1 md:hidden mt-2">
                            <div
                                class="email-input flex items-center justify-between p-2 pl-0 rounded cursor-pointer"
                                style="height: 50px"
                                @click="toggleOrderSummary"
                            >
                                <span
                                    class="text-itembody text-sm font-black select-none"
                                >
                                    {{ $t('checkout.orderSummary') }}
                                </span>
                                <dropdown-icon
                                    :class="{
                                        'rotate-180': showOrderSummary
                                    }"
                                    class="transition duration-300"
                                />
                            </div>
                            <checkout-basket
                                v-show="showOrderSummary"
                                :cart-items="cartItems"
                                :is-calculate="isCalculate"
                                :mobile="true"
                                class="mt-2"
                            />
                        </div>
                    </div>
                    <div v-if="paymentsEnabled" class="pt-6 px-8">
                        <span
                            class="block py-3 text-center text-gray-500 text-xs"
                        >
                            {{ $t('checkout.byClickingContinueYouAgree') }}
                            <router-link
                                :to="{ name: 'Privacy' }"
                                class="font-bold"
                            >
                                {{
                                    $t('checkout.termsAndConditions')
                                }} </router-link
                            >.
                        </span>
                    </div>
                </div>
                <checkout-basket
                    :cart-items="cartItems"
                    :is-calculate="isCalculate"
                    class="hidden md:block"
                />
            </div>
        </main>
        <order-notes-modal v-if="oderNotesVisibility" />
        <address-modal />
        <rewards-modal />
        <loyalty-logout-modal @confirm-logout="handleConfirmLogout" />
        <pay-with-piggy-gift-card-modal />
        <pay-como-points-modal
            :como-member-balance="comoMemberBalance"
            @get-como-member-balance="getComoMemberBalance"
        />
    </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import {
    required,
    email,
    requiredUnless,
    maxLength,
    minLength,
    requiredIf
} from 'vuelidate/lib/validators';
import isMobile from '@/helpers/isMobileDevice';
import isLocalStorageAvailable from '@/helpers/isLocalStorageAvailable';
import {
    FulfillmentMethodsEnum,
    PaymentMethodsEnum,
    TrackingEventsEnum
} from '@/enums';
import { preloadOrderStatus } from '@/utils/preloadComponents';
import addressRequirements from '@/util/addressRequirements';
import StoreNotice from '@/components/StoreNotice';
import TipSelection from '@/components/formElements/TipSelection';
import CustomerDetails from '@/components/formElements/CustomerDetails';
import DeliveryAddress from '@/components/formElements/DeliveryAddress';
import PreOrder from '@/components/formElements/PreOrder';
import PolicyConsents from '@/components/formElements/PolicyConsents';
import DiscountCodeBox from '@/components/formElements/DiscountCodeBox';
import GiftCardBox from '@/components/formElements/GiftCardBox';
import AdyenCheckout from '@/components/payments/AdyenCheckout';
const YocoCheckout = () => import('@/components/payments/YocoCheckout');
import OrderNotesModal from '@/components/modals/OrderNotesModal';
import OrderNotes from '@/components/formElements/OrderNotes';
import VenuePickupLocation from '@/components/formElements/VenuePickupLocation';
import PiggyText from '@/components/elements/PiggyText';
import PiggyCheckbox from '@/components/formElements/PiggyCheckbox';
import RewardsModal from '@/components/modals/RewardsModal';
import CutleryCheckbox from '@/components/formElements/CutleryCheckbox';
import AddressModal from '@/components/modals/DeliveryAddressModal';
import CheckoutBasket from '@/components/CheckoutBasket';
import GuestCountInput from '@/components/formElements/GuestCountInput';
import CountdownButton from '@/components/formElements/CountdownButton';
import DropdownIcon from '@/components/icons/DropdownIcon';
import ChevronLeftIcon from '@/components/icons/ChevronLeftIcon';
import PoNumber from '@/components/formElements/PoNumber';
import LoyaltyLogoutModal from '@/components/modals/LoyaltyLogoutModal';
import PayWithPiggyGiftCardModal from '@/components/modals/PayWithPiggyGiftCardModal';
import ClockIcon from '@/components/icons/ClockIcon.vue';
import { filterUserInfo } from '@/utils/customer';
import PayComoPointsModal from '@/components/modals/PayComoPointsModal';
import { calculateZonalDiscount } from '@/services/zonalService';
import { findDeepInvalid } from '@/helpers/findDeepInvalid';

const VALIDATION_CONFIG = {
    pickupDate: 'pickup-date',
    pickupTimeSlot: 'pickup-date',
    customer: 'customer-details',
    deliveryAddress: 'delivery-address',
    notes: 'order-notes'
};

export default {
    name: 'CheckoutView',
    head() {
        return {
            title: this.$t('meta.checkout')
        };
    },
    components: {
        ClockIcon,
        ChevronLeftIcon,
        DropdownIcon,
        AddressModal,
        CutleryCheckbox,
        StoreNotice,
        TipSelection,
        CustomerDetails,
        DeliveryAddress,
        PreOrder,
        DiscountCodeBox,
        GiftCardBox,
        AdyenCheckout,
        YocoCheckout,
        PolicyConsents,
        OrderNotes,
        OrderNotesModal,
        VenuePickupLocation,
        PiggyText,
        PiggyCheckbox,
        RewardsModal,
        CheckoutBasket,
        GuestCountInput,
        CountdownButton,
        PoNumber,
        LoyaltyLogoutModal,
        PayWithPiggyGiftCardModal,
        PayComoPointsModal
    },
    validations: {
        pickupDate: {
            required: requiredUnless('!isPreOrder')
        },
        pickupTimeSlot: {
            required: requiredUnless('!isPreOrder')
        },
        email: {
            required,
            email
        },
        firstName: {
            required
        },
        lastName: {
            required
        },
        phone: {
            required,
            minLength: minLength(7)
        },
        deliveryAddress: {
            buildingNumber: {
                required: requiredIf(function () {
                    if (
                        !FulfillmentMethodsEnum.isDeliveryMethod(
                            this.fulfillmentMethod
                        )
                    ) {
                        return false;
                    }

                    return this.hasField('buildingNumber');
                }),
                maxLength: maxLength(100)
            },
            street: {
                required: requiredIf(function () {
                    if (
                        !FulfillmentMethodsEnum.isDeliveryMethod(
                            this.fulfillmentMethod
                        )
                    ) {
                        return false;
                    }

                    return !(!this.hasField('street') && this.hasAutocomplete);
                }),
                maxLength: maxLength(100)
            },
            city: {
                required: requiredIf(function () {
                    if (
                        this.fulfillmentMethod !==
                        FulfillmentMethodsEnum.DELIVERY
                    ) {
                        return false;
                    }

                    return !(!this.hasField('city') && this.hasAutocomplete);
                }),
                maxLength: maxLength(100)
            },
            postCode: {
                required: requiredIf(function () {
                    if (
                        this.fulfillmentMethod !==
                        FulfillmentMethodsEnum.DELIVERY
                    ) {
                        return false;
                    }

                    return this.hasField('postCode');
                }),
                maxLength: maxLength(100)
            },
            notes: {
                required: requiredIf(function () {
                    if (
                        !FulfillmentMethodsEnum.isDeliveryMethod(
                            this.fulfillmentMethod
                        )
                    ) {
                        return false;
                    }

                    return this.$growthbook.isOn('require_delivery_notes');
                })
            }
        },
        customer: {
            firstName: {
                required: requiredIf(function () {
                    return this.userInformationSettings.firstName;
                })
            },
            lastName: {
                required: requiredIf(function () {
                    return this.userInformationSettings.lastName;
                })
            },
            email: {
                required: requiredIf(function () {
                    return this.userInformationSettings.email === 'required';
                }),
                email
            },
            phone: {
                required: requiredIf(function () {
                    return (
                        this.userInformationSettings.phone === 'enabled' ||
                        this.userInformationSettings.phone === 'validated'
                    );
                }),
                minLength: minLength(7)
            },
            companyName: {
                required: requiredIf(function () {
                    return (
                        this.userInformationSettings.companyName === 'required'
                    );
                })
            }
        },
        notes: {
            required: requiredIf(function () {
                return (
                    this.$store.state.venue.venue.enableOrderNotes ===
                        'mandatory' && this.notes.length === 0
                );
            })
        }
    },
    data() {
        return {
            isLoading: false,
            globalLoading: false,
            paymentError: false,
            checkoutError: false,
            discountCode: null,
            discountCodeError: null,
            discountCodeErrorMessage: null,
            showDiscount: false,
            discountIsLoading: false,
            errors: '',
            adyenRedirectResult: '',
            disableAll: false,
            isCalculate: false,
            phoneValidationError: false,
            isCounting: false,
            showOrderSummary: false,
            zonalIsLoading: false,
            isAnimationLocked: false
        };
    },
    async created() {
        if (this.deliveryPostCode) {
            this.setPostCode(this.deliveryPostCode);
        }

        if (this.hasTooLowAmount || this.hasTooHighAmount) {
            this.$router.go(-1);
        }

        const menuVersionCorrect = await this.checkMenuVersion();

        if (!menuVersionCorrect) {
            this.$router.go(-1);
        }
        preloadOrderStatus();
    },

    async mounted() {
        if (this.isPreOrder) {
            this.$store.commit('cart/setPreOrder', true);
        }

        if (!this.fulfillmentMethod && !this.$route.params.orderId) {
            console.error('Fulfillment method not set');
        }

        if (
            (this.cartItems.length === 0 && !this.$route.params.orderId) ||
            (!this.selectedTableId &&
                this.isInStore &&
                !this.$route.params.orderId)
        ) {
            await this.$router.replace({ name: 'menu' });
        }

        if (!this.zonalBasketId) {
            await this.calculateZonalDiscountMethod();
        }

        await this.cartTotalWithDiscount();

        if (this.activePaymentSystem === PaymentMethodsEnum.ADYEN) {
            await this.checkAdyenRedirect();
        }

        this.isCalculate = true;
        await this.calculatePiggyCredits(this.totalWithDiscount / 100);

        if (this.$analytics) {
            const totalNumeric = parseFloat(
                (this.totalWithDiscount / 100).toFixed(2)
            );

            await this.$analytics.track(TrackingEventsEnum.STARTED_CHECKOUT, {
                currency: this.currencyCode,
                items: this.cartItems,
                venueSlug: this.$store.state.venue?.venue?.slug,
                value: totalNumeric
            });
        }
    },
    methods: {
        ...mapActions({
            checkout: 'cart/checkout',
            cartTotalWithDiscount: 'cart/cartTotalWithDiscount',
            checkMenuVersion: 'venue/checkMenuVersion',
            setPaymentType: 'cart/setPaymentType',
            setDeliveryAddress: 'deliveryAddress/setAddress',
            setPostCode: 'deliveryAddress/setPostCode',
            catchOrder: 'cart/catchOrder',
            calculateEstimatedCredits: 'customer/calculateEstimatedCredits',
            setAuth: 'customer/setAuth',
            clearRewards: 'customer/clearRewardsAndDiscounts'
        }),
        async validateAndCheckout(paymentType) {
            if (this.isLoading) {
                return false;
            }

            this.isLoading = true;

            try {
                if (
                    this.cartItems.length === 0 ||
                    this.hasTooLowAmount ||
                    this.hasTooHighAmount ||
                    (this.isPreOrder && !this.pickupDateValidated())
                ) {
                    return false;
                }

                await this.setPaymentType(paymentType);

                const isValid =
                    this.customerValidated() && this.orderNotesValidated();

                if (this.isPickup || this.isInStore) {
                    if (!isValid) return false;
                } else if (this.isDelivery) {
                    if (!isValid || !this.deliveryAddressValidated())
                        return false;
                } else {
                    return false;
                }

                await this.checkout();
            } catch (e) {
                this.checkoutError = true;
                this.disableAllFields(false);
                return false;
            } finally {
                this.isLoading = false;
            }
        },
        animateError(field) {
            if (this.isAnimationLocked) {
                return;
            }

            if (
                typeof this.$v[field] === 'object' &&
                this.$v[field].$invalid &&
                field !== 'deliveryAddress'
            ) {
                const nestedInvalid = findDeepInvalid(this.$v[field]);

                if (nestedInvalid) {
                    field = nestedInvalid;
                }
            }

            field =
                field === 'deliveryAddress' ? VALIDATION_CONFIG[field] : field;

            const element = document.getElementById(field);

            if (element) {
                this.isAnimationLocked = true;
                element.classList.add('wobble');
                setTimeout(() => {
                    element.classList.remove('wobble');
                    this.isAnimationLocked = false;
                }, 1600);
            }
        },
        validate(fields) {
            const fieldList = Array.isArray(fields) ? fields : [fields];

            fieldList.forEach(field => this.$v[field]?.$touch());

            const invalidField =
                fieldList.find(field => this.$v[field]?.$invalid) ||
                (this.phoneValidationError &&
                    fieldList.includes('customer') &&
                    'customer');

            if (invalidField) {
                this.errors = 'ERROR';
                this.isLoading = false;
                this.scrollToInvalidField(VALIDATION_CONFIG[invalidField]);
                console.error(
                    `[${invalidField.toUpperCase()}] Group validation error ${invalidField}`
                );
                this.$nextTick(() => this.animateError(invalidField));

                return false;
            }

            return true;
        },
        pickupDateValidated() {
            return this.validate(['pickupDate', 'pickupTimeSlot']);
        },
        customerValidated() {
            return this.validate('customer');
        },
        deliveryAddressValidated() {
            return this.validate('deliveryAddress');
        },
        orderNotesValidated() {
            return this.validate('notes');
        },
        phoneValidation(value) {
            this.phoneValidationError = value;
        },
        clearErrors(initCheck) {
            this.errors = '';
            this.checkoutError = null;

            if (initCheck) {
                this.$v.$reset();
            }
        },
        scrollToInvalidField(id) {
            const element = document.getElementById(id);

            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        },
        isValidated() {
            this.clearErrors(false);

            if (!this.cartItems.length) {
                console.error('Cart is empty');
                return false;
            }

            if (this.isPreOrder) {
                this.pickupDateValidated();
            }
            if (this.isPickup || this.isInStore) {
                this.customerValidated();
                this.orderNotesValidated();
            } else {
                this.customerValidated();
                this.deliveryAddressValidated();
                this.orderNotesValidated();
            }

            return !this.errors;
        },
        async checkAdyenRedirect() {
            this.adyenRedirectResult = this.$route.query.redirectResult
                ? decodeURIComponent(this.$route.query.redirectResult)
                : '';

            if (this.adyenRedirectResult) {
                this.globalLoading = true;

                this.$modal.hide('fulfillment-method-modal');

                await this.catchOrder({
                    orderId: this.$route.params.orderId,
                    venueId: this.venueId,
                    isPaymentRedirect: true
                });

                this.globalLoading = false;
            }
        },
        disableAllFields(value) {
            this.disableAll = value;
        },
        async calculatePiggyCredits(unitValue) {
            if (!this.piggySettings?.enabled) {
                return;
            }

            const params = {
                email: this.email,
                piggyShopId: this.piggySettings.piggyShopId,
                unitValue,
                accountId: this.accountId,
                unitName: this.piggySettings.creditUnitName || '',
                venueId: this.venueId
            };

            await this.calculateEstimatedCredits(params);

            this.isCalculate = false;
        },
        reloadPage() {
            window.location.reload();
        },
        toggleCounting() {
            this.isCounting = !this.isCounting;
        },
        toggleOrderSummary() {
            this.showOrderSummary = !this.showOrderSummary;
        },
        handleConfirmLogout() {
            this.setAuth({ accountId: this.accountId, value: false });
            this.clearRewards();
            this.cartTotalWithDiscount();
        },
        async getComoMemberBalance() {
            try {
                const { data } = await this.$axios.post(`/como/check-balance`, {
                    ...this.customerIdentifier,
                    identifierType: this.identifierType,
                    venueId: this.venueId,
                    accountId: this.accountId
                });

                this.$store.commit('customer/SET_COMO_DATA', {
                    memberPoints: data.monetary || 0
                });
            } catch (error) {
                throw new Error(`API ${error}`);
            }
        },
        customerIdentifier() {
            return this.identifierType === 'phone'
                ? { phone: this.phone }
                : { email: this.email };
        },
        async calculateZonalDiscountMethod() {
            if (!this.zonalEnabled) {
                return;
            }

            this.zonalIsLoading = true;

            const payload = {
                accountId: this.accountId,
                venueId: this.venueId,
                fulfillmentMethod: this.fulfillmentMethod,
                items: this.cartItems,
                ...(this.selectedTableId
                    ? { tableId: this.selectedTableId }
                    : {})
            };

            try {
                const { discountAmount, basketId } =
                    await calculateZonalDiscount(payload);

                if (Number.isFinite(discountAmount)) {
                    this.$store.commit(
                        'cart/SET_ZONAL_DISCOUNT_SUM',
                        discountAmount
                    );
                }
                this.$store.commit('cart/SET_ZONAL_BASKET_ID', basketId);

                if (discountAmount > 0) {
                    this.$notify({
                        group: 'PiggyRewards',
                        title: `£${(discountAmount / 100).toFixed(2)} promotion applied!`,
                        type: 'error'
                    });
                }
            } catch (error) {
                const message = error?.response?.data?.message;

                if (message === 'sold_out') {
                    this.$store.dispatch(
                        'cart/setOrderCurrentCheckoutError',
                        true
                    );

                    this.$store.dispatch('cart/setSoldOutItems', {
                        items: error?.response?.data?.data,
                        redirect: true
                    });
                } else if (message === 'ordering_disabled') {
                    this.$modal.show('custom-message-modal', {
                        text: 'orderingDisabled'
                    });
                    this.$router.push({ name: 'menu' });
                }
            } finally {
                this.zonalIsLoading = false;
            }
        }
    },
    computed: {
        PaymentMethodsEnum: () => PaymentMethodsEnum,
        ...mapState({
            // Cart
            cartItems: state => state.cart.items,
            fulfillmentMethod: state => state.cart.fulfillmentMethod,
            piggyProductDiscount: state => state.cart.piggyProductDiscount,
            totalPiggyDiscount: state => state.cart.totalPiggyDiscount,
            rewardSelected: state => state.cart.piggyRewardSelected,
            selectedTableId: state => state.cart.selectedTableId,
            zoneDeliveryMinCost: state => state.cart.zoneDeliveryMinCost,
            zonalBasketId: state => state.cart.zonalBasketId,

            // Venue
            currencyCode: state => state.venue.venue.currencyCode,
            venueId: state => state.venue.venue.id,
            availablePreorderDays: state =>
                state.venue.venue.availablePreorderDays,
            minDeliveryAmount: state => state.venue.venue.minDeliveryAmount,
            minPickupAmount: state => state.venue.venue.minPickupAmount,
            maxDeliveryAmount: state => state.venue.venue.maxDeliveryAmount,
            maxPickupAmount: state => state.venue.venue.maxPickupAmount,
            paymentsEnabled: state => state.venue.venue.paymentsEnabled,
            serviceCharge: state => state.venue.venue.serviceCharge,
            acceptsPreOrders: state => state.venue.venue.acceptsPreOrders,
            enableOrderNotes: state => state.venue.venue.enableOrderNotes,
            activePaymentSystem: state => state.venue.venue.activePaymentSystem,
            defaultServiceChargeEnabled: state =>
                state.venue.venue.defaultServiceChargeEnabled,
            piggySettings: state => state.venue.venue.piggySettings,
            pickupSlots: state => state.venue.venue.pickupSlots,
            accountId: state => state.venue.venue.accountId,
            optInMessage: state => state.venue.venue.optInMessage,
            comoEnabled: state => state.venue.venue.comoEnabled,
            zonalEnabled: state => state.venue.venue.zonalEnabled,

            // Customer
            firstName: state => state.customer.firstName,
            lastName: state => state.customer.lastName,
            email: state => state.customer.email,
            phone: state => state.customer.phone,
            companyName: state => state.customer.companyName,
            notes: state => state.customer.notes,
            transactionId: state => state.customer.como.transactionId,
            openTime: state => state.customer.como.openTime,
            comoDiscountSum: state => state.customer.como.discountSum,
            deals: state => state.customer.como.deals,
            redeemAssets: state => state.customer.como.redeemAssets,
            piggyCheckbox: state => state.customer.piggy.loyaltyCheckboxState,
            identifierType: state => state.customer.identifierType,
            comoPaymentWithPointsEnabled: state =>
                state.customer.como.usedByPayment,
            comoPaymentAmount: state => state.customer.como.paymentAmount,
            comoMemberBalance: state => state.customer.como.memberPoints,

            // Bill
            billId: state => state.bill.id
        }),
        ...mapGetters({
            customerAuth: 'customer/auth',
            cartSubtotal: 'cart/cartSubtotal',
            cartTotal: 'cart/cartTotal',
            totalWithDiscount: 'cart/cartTotalWithDiscount',
            deliveryPostCode: 'cart/deliveryPostCode',
            findTableById: 'venue/findTableById',
            policyAgreed: 'customer/policyAgreed',
            discountCodeDetails: 'discount/details',
            deliveryAddress: 'deliveryAddress/address',
            prepTime: 'venue/prepTime',
            userInformationSettings: 'venue/userInformationSettings',
            askAboutCutlery: 'venue/cutlerySetting',
            pickupDate: 'cart/getPickupDate',
            pickupTimeSlot: 'cart/getPickupTimeSlot',
            preOrderDateTime: 'cart/getPreOrderDateTimeSlot',
            isPatEnabled: 'venue/isPatEnabled',
            availableTips: 'cart/availableTips'
        }),
        canAddToBill() {
            const allowedVenueIds = [
                8376,
                9708,
                9710,
                9745,
                9746,
                9714,
                9711,
                9712,
                9713,
                10243, // PP
                10175, // Kanada ya
                10176,
                10177,
                10178,
                6634, // organic market
                10467, // Perch
                10466, // Perch
                10438, // Perch
                10437, // Perch
                6634,
                10548, // tonkotsu
                7965, // test
                10512, // Machiya
                7965,
                9805, // FOU
                10822, // PP
                10182, //KY
                10181,
                10480, // CL
                10474,
                10473,
                10478,
                10353,
                10476,
                10475,
                9747,
                10477,
                10479,
                10481,
                10472,
                10471,
                10950,
                10997,
                10470,
                6324, // P&B
                11419
            ];

            return (
                this.isPatEnabled &&
                (!!this.billId || allowedVenueIds.includes(this.venueId))
            );
        },
        fulfillmentMethodLabel() {
            return {
                [FulfillmentMethodsEnum.IN_STORE]: this.$t(
                    'fulfillmentMethods.InStore'
                ),
                [FulfillmentMethodsEnum.DELIVERY]: this.$t(
                    'fulfillmentMethods.Delivery'
                ),
                [FulfillmentMethodsEnum.PICKUP]: this.$t(
                    'fulfillmentMethods.Pickup'
                )
            }[this.fulfillmentMethod];
        },
        selectedMethodPreorderDays() {
            if (
                FulfillmentMethodsEnum.isDeliveryMethod(this.fulfillmentMethod)
            ) {
                return this.availablePreorderDays.deliveryDates;
            }

            return this.availablePreorderDays.pickupDates;
        },
        customer() {
            const { validFields } = filterUserInfo(
                this.userInformationSettings,
                {
                    firstName: this.firstName,
                    lastName: this.lastName,
                    email: this.email,
                    phone: this.phone,
                    companyName: this.companyName
                }
            );

            const customer = {
                notes: this.notes,
                policyAgreed: this.policyAgreed(),
                piggy: {
                    credits: 0,
                    checkbox: this.piggyCheckbox,
                    piggyProductDiscount: this.totalPiggyDiscount,
                    rewardSelected: this.rewardSelected
                },
                ...validFields
            };

            if (this.comoDiscountSum > 0 || this.comoEnabled) {
                customer.como = {
                    isAuth: this.customerAuth,
                    transactionId: this.transactionId,
                    openTime: this.openTime,
                    discountSum: this.comoDiscountSum,
                    deals: this.deals,
                    redeemAssets: this.redeemAssets ? this.redeemAssets : [],
                    identifierType: this.identifierType,
                    comoPaymentAmount: this.comoPaymentAmount
                };
            }

            return customer;
        },
        isPreOrder() {
            return this.acceptsPreOrders && this.pickupSlots.length > 0;
        },
        checkoutObject() {
            return {
                customer: this.customer,
                deliveryAddress: this.deliveryAddress,
                fulfillmentMethod: this.fulfillmentMethod,
                venueId: this.venueId,
                total: this.cartTotal,
                preOrderDateTime: this.preOrderDateTime || null
            };
        },
        isPickup() {
            return FulfillmentMethodsEnum.isPickupMethod(
                this.fulfillmentMethod
            );
        },
        isInStore() {
            return this.fulfillmentMethod === FulfillmentMethodsEnum.IN_STORE;
        },
        isDelivery() {
            return FulfillmentMethodsEnum.isDeliveryMethod(
                this.fulfillmentMethod
            );
        },
        minimumDeliveryAmount() {
            if (this.zoneDeliveryMinCost) {
                return this.zoneDeliveryMinCost;
            } else {
                return this.minDeliveryAmount;
            }
        },
        hasTooLowAmount() {
            return (
                (this.isDelivery &&
                    this.minimumDeliveryAmount > this.cartSubtotal) ||
                (this.isPickup && this.minPickupAmount > this.cartSubtotal)
            );
        },
        hasTooHighAmount() {
            return (
                (this.isDelivery &&
                    this.maxDeliveryAmount &&
                    this.maxDeliveryAmount < this.cartSubtotal) ||
                (this.isPickup &&
                    this.maxPickupAmount &&
                    this.maxPickupAmount < this.cartSubtotal)
            );
        },
        table() {
            return this.findTableById(this.selectedTableId);
        },
        policyAgreedInLocalStorage() {
            if (!isLocalStorageAvailable()) {
                return false;
            }

            return (
                localStorage.getItem(`sk_optin_${this.accountId}`) &&
                localStorage.getItem(`sk_optin_${this.accountId}`) === 'true'
            );
        },
        showTipSelection() {
            return (
                this.availableTips &&
                !this.billId &&
                (!this.defaultServiceChargeEnabled || this.adyenRedirectResult)
            );
        },
        hasAutocomplete() {
            return this.$growthbook.isOn('address_autocomplete');
        },
        isPoNumberEnabled() {
            return this.$growthbook.isOn('po_number');
        },
        hasField() {
            return fieldName => {
                return this.requiredFields.includes(fieldName);
            };
        },
        requiredFields() {
            return (
                addressRequirements.countries[this.deliveryAddress.country]
                    ?.fields || addressRequirements.default.fields
            );
        },
        showEstimatedCredits() {
            return !!(
                this.piggySettings?.enabled &&
                !this.piggySettings.loyaltyOptIn.enabledLoyalty
            );
        },
        oderNotesVisibility() {
            return this.enableOrderNotes !== 'hidden';
        },
        isMobile() {
            return isMobile();
        },
        hasGuestCountInput() {
            return this.$growthbook.isOn('guest_count');
        },
        hasAddToBillOrder() {
            return this.$growthbook.isOn('add_to_bill_order');
        }
    }
};
</script>

<style src="@/assets/css/checkout.css"></style>
<style scoped src="@/assets/css/checkout_scoped.css"></style>
